import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import Chart from "react-apexcharts";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMDonutChartCategory {
  /** (string) The label for this slice */
  label: string;
  /** (number) The numeric value for this slice */
  value: number;
  /** (string) Fill color for this slice */
  color: string;
}

export interface PMDonutChartCardProps extends Omit<
  PMBaseCardProps,
  "children"
> {
  /** (PMDonutChartCategory[]) The data to plot in the donut */
  data: PMDonutChartCategory[];
  /** Optional click handler for the chart */
  onCardClick?: () => void;
  /** Optional click handler for individual segments */
  onSegmentClick?: (segmentIndex: number, segmentLabel: string) => void;
  /** Optional formatter for values shown in the legend and tooltip */
  valueFormatter?: (value: number) => string;
  /** Optional max width for the legend column on non-mobile screens */
  legendColumnMaxWidth?: string;
  /** Optional max width for the legend item list */
  legendItemsMaxWidth?: string;
}

export const PMDonutChartCard: PimoReactComponent<PMDonutChartCardProps> = ({
  data,
  icon = "DonutLarge",
  title = "Donut Chart",
  showInfoIcon = false,
  showMenuIcon = false,
  fixedWidth = "100%",
  fixedHeight,
  onCardClick,
  onSegmentClick,
  valueFormatter = String,
  legendColumnMaxWidth = "200px",
  legendItemsMaxWidth = "180px",
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const total = data.reduce((sum, entry) => sum + entry.value, 0);
  const series = data.map((entry) => entry.value);
  const labels = data.map((entry) => entry.label);
  const colors = data.map((entry) => entry.color);
  const formatValue = (value: number) => valueFormatter(value);

  const isEmptySeries =
    series.length === 0 || series.every((v) => v === 0 || v == null);

  const chartSize = isMobile
    ? "100px"
    : isTablet
      ? "120px"
      : isSmallScreen
        ? "140px"
        : "180px";
  const donutSize = isMobile
    ? "50%"
    : isTablet
      ? "55%"
      : isSmallScreen
        ? "60%"
        : "75%";

  const chartSizeNum = parseInt(chartSize);
  const dynamicHeight = `${chartSizeNum + 80}px`;

  const options = {
    chart: {
      type: "donut" as const,
      toolbar: { show: false },
      ...(onSegmentClick && {
        events: {
          dataPointSelection: (
            event: MouseEvent,
            _chartContext: ApexCharts | undefined,
            config: ApexCharts.ApexChartEventOpts | undefined
          ) => {
            event.stopPropagation();
            const segmentIndex = config?.dataPointIndex ?? 0;
            const segmentLabel = labels[segmentIndex];
            onSegmentClick(segmentIndex, segmentLabel);
          },
        },
      }),
    },
    labels,
    colors,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    tooltip: {
      y: {
        formatter: (val: number) =>
          total > 0
            ? `${formatValue(val)} (${Math.round((val / total) * 100)}%)`
            : formatValue(val),
      },
      fixed: {
        enabled: true,
        position: "topRight",
      },
    },
    plotOptions: {
      pie: { donut: { size: donutSize } },
    },
  };

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={fixedHeight || dynamicHeight}
      overflow="visible"
      {...rest}
    >
      <Grid
        container
        spacing={isMobile ? 2 : 3}
        sx={{
          width: "100%",
          height: "100%",
          p: isMobile ? 1 : 2,
          boxSizing: "border-box",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid
          xs={12}
          md={6}
          onClick={onCardClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: chartSize,
            cursor: onCardClick ? "pointer" : "default",
          }}
        >
          {isEmptySeries ? (
            <Typography color="text.secondary">No data</Typography>
          ) : (
            <Chart
              options={options}
              series={series}
              type="donut"
              height={chartSize}
              width={chartSize}
            />
          )}
        </Grid>

        <Grid
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: isMobile ? "100%" : legendColumnMaxWidth,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
              maxWidth: legendItemsMaxWidth,
            }}
          >
            {data?.slice(0, 5)?.map((entry, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMobile ? 0.5 : 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: isMobile ? 6 : 8,
                    height: isMobile ? 6 : 8,
                    bgcolor: entry.color,
                    borderRadius: 0,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  {entry.label}: {formatValue(entry.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </PMBaseCard>
  );
};

export default PMDonutChartCard;
